import React, { useState, useEffect } from 'react';
import * as systemManagementService from '../../../services/systemManagementService';
import { AddIcon, EditIcon, DeleteIcon } from './Icons';

const TechnicalAssistanceTypes = ({ onSnackbarMessage }) => {
  const [taTypes, setTaTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTaType, setEditingTaType] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [taTypeToDelete, setTaTypeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTaTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await systemManagementService.getTATypes();
      setTaTypes(response.data);
    } catch (err) {
      setError(err.message);
      onSnackbarMessage('Error loading technical assistance types', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaTypes();
  }, []);

  // Filter and pagination logic
  const filteredTaTypes = taTypes.filter(taType =>
    taType.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTaTypes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTaTypes = filteredTaTypes.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTaType) {
        await systemManagementService.updateTAType(editingTaType.id, formData);
        onSnackbarMessage('Technical assistance type updated successfully', 'success');
      } else {
        await systemManagementService.createTAType(formData);
        onSnackbarMessage('Technical assistance type created successfully', 'success');
      }
      setShowModal(false);
      setEditingTaType(null);
      setFormData({ name: '' });
      await fetchTaTypes();
    } catch (err) {
      onSnackbarMessage(err.message || 'Error saving technical assistance type', 'error');
    }
  };

  const handleEdit = (taType) => {
    setEditingTaType(taType);
    setFormData({
      name: taType.name
    });
    setShowModal(true);
  };

  const handleDeleteClick = (taType) => {
    setTaTypeToDelete(taType);
    setDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await systemManagementService.deleteTAType(taTypeToDelete.id);
      onSnackbarMessage('Technical assistance type deleted successfully', 'success');
      await fetchTaTypes();
      setDeleteConfirmModal(false);
      setTaTypeToDelete(null);
    } catch (err) {
      onSnackbarMessage(err.message || 'Error deleting technical assistance type', 'error');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingTaType(null);
    setFormData({ name: '' });
  };

  const handleAddNew = () => {
    setEditingTaType(null);
    setFormData({ name: '' });
    setShowModal(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold font-poppins text-black">Technical Assistance Types</h2>
          <p className="text-gray-600 font-poppins mt-1">Manage different types of technical assistance requests</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins shadow-sm"
        >
          <AddIcon className="w-4 h-4 mr-2" />
          Add Technical Assistance Type
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 font-poppins">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search technical assistance types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
            />
          </div>
          <div className="text-sm text-gray-600 font-poppins flex items-center">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredTaTypes.length)} of {filteredTaTypes.length} technical assistance types
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                  Created
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="3" className="px-6 py-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600 font-poppins">Loading technical assistance types...</p>
                  </td>
                </tr>
              ) : currentTaTypes.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-6 py-12 text-center text-gray-500 font-poppins">
                    {searchTerm ? 'No technical assistance types found matching your search.' : 'No technical assistance types found. Add your first technical assistance type to get started.'}
                  </td>
                </tr>
              ) : (
                currentTaTypes.map((taType) => (
                  <tr key={taType.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-poppins">
                        {taType.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-poppins">
                      {new Date(taType.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleEdit(taType)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Edit technical assistance type"
                        >
                          <EditIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(taType)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete technical assistance type"
                        >
                          <DeleteIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 font-poppins">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-md font-poppins ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 font-poppins text-black">
                {editingTaType ? 'Edit Technical Assistance Type' : 'Add New Technical Assistance Type'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                    Technical Assistance Type Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                    placeholder="Enter technical assistance type name"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins"
                  >
                    {editingTaType ? 'Update Technical Assistance Type' : 'Add Technical Assistance Type'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-poppins"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 font-poppins text-black">Confirm Delete</h3>
              <p className="text-gray-600 font-poppins mb-6">
                Are you sure you want to delete "{taTypeToDelete?.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-poppins"
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setDeleteConfirmModal(false);
                    setTaTypeToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicalAssistanceTypes;

